import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Firms from './pages/Firms';
import Projects from './pages/Projects';
import Cases from './pages/Cases';
import CaseView from './pages/CaseView';
import FirmRegistration from './pages/FirmRegistration';
import FirmApproval from './pages/FirmApproval';
import FirmDashboard from './pages/FirmDashboard';
import FirmClientEstimates from './pages/FirmClientEstimates';
import ClientEstimatesList from './pages/ClientEstimatesList';
import ProjectCreate from './pages/ProjectCreate';
import ProjectView from './pages/ProjectView';
import ProjectEdit from './pages/ProjectEdit';
import Layout from './components/Layout';
import FirmMembers from './pages/FirmMembers';
import { AgreementGate } from './components/AgreementGate';
import AdminAgreements from './pages/AdminAgreements';
import AdminAgreementEdit from './pages/AdminAgreementEdit';
import FirmLegal from './pages/FirmLegal';
import CaseForm from './pages/CaseForm';
import CaseEdit from './pages/CaseEdit';
import VendorAssign from './pages/VendorAssign';
import ProfileManagement from './pages/ProfileManagement';
import PendingEstimates from './pages/PendingEstimates';
import VendorInvoices from './pages/VendorInvoices';
import VendorInvoiceView from './pages/VendorInvoiceView';
import CustomInvoiceView from './pages/CustomInvoiceView';
import MyInvoices from './pages/MyInvoices';
import TdsPayments from './pages/TdsPayments';
import MasqueradeUsers from './pages/MasqueradeUsers';
import ProfileEdit from './pages/ProfileEdit';
import UserManagement from './pages/UserManagement';
import EmailSettings from './pages/EmailSettings';
import SystemSettings from './pages/SystemSettings';
import LegalSection from './pages/LegalSection';
import UserTimeReport from './pages/UserTimeReport';
import UserTimeSheet from './pages/UserTimeSheet';
import UserCheckinReport from './pages/UserCheckinReport';
import VendorAssignmentReport from './pages/VendorAssignmentReport';
import DailyAssignmentReport from './pages/DailyAssignmentReport';
import TimeTrackerReport from './pages/TimeTrackerReport';
import ActiveTimerReport from './pages/ActiveTimerReport';
import IdleTimeActivityReport from './pages/IdleTimeActivityReport';
import MonthlyTimerLogReport from './pages/MonthlyTimerLogReport';
import MonthlyCaseBillabilityReport from './pages/MonthlyCaseBillabilityReport';
import ResetPassword from './pages/ResetPassword';
import ClientInvoices from './pages/ClientInvoices';
import ClientInvoiceView from './pages/ClientInvoiceView';
import PendingClientEstimates from './pages/PendingClientEstimates';
import ClientFirmSettings from './pages/ClientFirmSettings';
import ClientFirmWallet from './pages/ClientFirmWallet';
import AutoPayManagement from './pages/AutoPayManagement';
import EmailLogs from './pages/EmailLogs';
import BackendLogs from './pages/BackendLogs';
import UserActivityLogs from './pages/UserActivityLogs';
import TimerConfiguration from './pages/TimerConfiguration';
import DocumentReview from './pages/DocumentReview';
import AuthCallback from './pages/AuthCallback';
import HelpSupport from './pages/HelpSupport';
import JiraTimeLogs from './pages/JiraTimeLogs';
import AppVersionChecker from './components/AppVersionChecker';

// Create theme based on hogo-theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      fontSize: '1.75rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
      '@media (max-width:600px)': {
        fontSize: '1.1rem',
      },
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          minHeight: 44, // Touch-friendly size
          padding: '8px 16px',
          '@media (max-width:600px)': {
            minHeight: 48,
            padding: '10px 16px',
            fontSize: '0.95rem',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '@media (max-width:600px)': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width:600px)': {
            margin: 8,
            maxWidth: 'calc(100% - 16px)',
            maxHeight: 'calc(100% - 16px)',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          },
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AgreementGate>
      {children}
    </AgreementGate>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppVersionChecker />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/firm-registration" element={<FirmRegistration />} />
            
            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="firms" element={<Firms />} />
              <Route path="firms/:firmId" element={<FirmDashboard />} />
              <Route path="firms/:firmId/members" element={<FirmMembers />} />
              <Route path="firms/:firmId/client-estimates" element={<FirmClientEstimates />} />
              <Route path="firms/:firmId/all-client-estimates" element={<ClientEstimatesList />} />
              <Route path="firms/:firmId/legal" element={<FirmLegal />} />
              <Route path="firms/:firmId/settings" element={<ClientFirmSettings />} />
              <Route path="firms/:firmId/wallet" element={<ClientFirmWallet />} />
              <Route path="firms/:firmId/autopay" element={<AutoPayManagement />} />
              <Route path="firms/:firmId/projects" element={<Projects />} />
              <Route path="firms/:firmId/projects/new" element={<ProjectCreate />} />
              <Route path="firms/:firmId/projects/:projectId" element={<ProjectView />} />
              <Route path="firms/:firmId/projects/:projectId/edit" element={<ProjectEdit />} />
              <Route path="projects/:projectId" element={<ProjectView />} />
              <Route path="projects/:projectId/edit" element={<ProjectEdit />} />
              <Route path="firm-approval" element={<FirmApproval />} />
              <Route path="projects" element={<Projects />} />
              <Route path="cases" element={<Cases />} />
              <Route path="cases/:caseId/document-review/:fileId" element={<DocumentReview />} />
              <Route path="firms/:firmId/cases" element={<Cases />} />
              <Route path="firms/:firmId/cases/:caseId" element={<CaseView />} />
              <Route path="firms/:firmId/cases/:caseId/document-review/:fileId" element={<DocumentReview />} />
              <Route path="firms/:firmId/cases/new" element={<CaseForm mode="create" />} />
              <Route path="firms/:firmId/cases/:caseId/edit" element={<CaseEdit />} />
              <Route path="firms/:firmId/vendor-assign" element={<VendorAssign />} />
              <Route path="profile" element={<ProfileManagement />} />
              <Route path="profile/edit" element={<ProfileEdit />} />
              <Route path="users/:userId/profile" element={<ProfileManagement />} />
              <Route path="pending-estimates" element={<PendingEstimates />} />
              <Route path="vendor-invoices" element={<VendorInvoices />} />
              <Route path="vendor-invoices/:invoiceId" element={<VendorInvoiceView />} />
              <Route path="custom-invoices/:invoiceId" element={<CustomInvoiceView />} />
              <Route path="my-invoices" element={<MyInvoices />} />
              <Route path="client-invoices" element={<ClientInvoices />} />
              <Route path="client-invoices/:invoiceId" element={<ClientInvoiceView />} />
              <Route path="pending-client-estimates" element={<PendingClientEstimates />} />
              <Route path="tds-payments" element={<TdsPayments />} />
              <Route path="tds-payments/:paymentId" element={<TdsPayments />} />
              <Route path="admin/agreements" element={<AdminAgreements />} />
              <Route path="admin/agreements/:agreementId" element={<AdminAgreementEdit />} />
              <Route path="masquerade-users" element={<MasqueradeUsers />} />
              <Route path="user-management" element={<UserManagement />} />
              <Route path="email-settings" element={<EmailSettings />} />
              <Route path="system-settings" element={<SystemSettings />} />
              <Route path="timer-configuration" element={<TimerConfiguration />} />
              <Route path="email-logs" element={<EmailLogs />} />
              <Route path="backend-logs" element={<BackendLogs />} />
              <Route path="user-activity-logs" element={<UserActivityLogs />} />
              <Route path="legal-section" element={<LegalSection />} />
              <Route path="reports/user-time" element={<UserTimeReport />} />
              <Route path="reports/user-checkin" element={<UserCheckinReport />} />
              <Route path="reports/user-time-sheet" element={<UserTimeSheet />} />
              <Route path="reports/vendor-assignment" element={<VendorAssignmentReport />} />
              <Route path="reports/daily-assignment" element={<DailyAssignmentReport />} />
              <Route path="reports/time-tracker" element={<TimeTrackerReport />} />
              <Route path="reports/active-timers" element={<ActiveTimerReport />} />
              <Route path="reports/idle-time-activity" element={<IdleTimeActivityReport />} />
              <Route path="reports/monthly-timer-log" element={<MonthlyTimerLogReport />} />
              <Route path="reports/monthly-case-billability" element={<MonthlyCaseBillabilityReport />} />
              <Route path="reports/jira-timelogs" element={<JiraTimeLogs />} />
              <Route path="help-support" element={<HelpSupport />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 